import { createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { CommonState, LoadingState, Row } from '~libs/reduxUtils';

import {
  acknowledge,
  cancel,
  create,
  endBidding,
  getAll,
  getEstimate,
  getOne,
  getSignature,
  getStatistics,
  notifyUser,
  startBidding,
  typePrefix,
  update,
} from '~appointments/actions/appointments';

interface appointmentState extends CommonState<Row> {
  rowSignature?: string | null;
  total?: number;
  estimate?: { first: Row[]; last: Row[]; count: number | null };
  message: string | null;
  copyRow?: any;
  statistics?: {
    daily?: object;
    status?: object;
  };
  notification?: {
    type: string;
    success: boolean;
    reason: string;
    allow_override: boolean;
  };
  statisticsLoading: LoadingState;
  rowLoading: LoadingState;
  childRow?: any;
}

export interface LinkedAppointment {
  id: string;
  full_name: string;
  start_time: string;
  end_time: string;
  service_type: string;
  user_id?: number;
  client_id?: number;
}

const initialState: appointmentState = {
  loading: 'idle',
  statisticsLoading: 'idle',
  rowLoading: 'idle',
  error: null,
  rows: [],
  total: 0,
  message: null,
};

const copyRow = (state: appointmentState, action: any, alert = true) => {
  const row = { ...action.payload };
  if (alert) {
    state.message = `Appointment #${row.reference_number} copied! Check and save the appointment to confirm creation.`;
  }
  // Clear ignore variables;
  const ignoreKeys = [
    'id',
    'reference_number',
    'status_id',
    'status',
    'repeat',
    'transport_dropoff',
    'transport_pickup',
  ];
  ignoreKeys.forEach((key) => delete row[key]);

  state.copyRow = row;
};

const createChild = (state: appointmentState, action: any) => {
  const row = { ...action.payload };
  state.message = `Parent #${row.reference_number} copied! Check and save the appointment to confirm creation.`;

  const childRow = { ...row, parent_repeat_id: row.id };

  const copyKeys = [
    'accounts_definitions',
    'qualification_level',
    'service_type_id',
    'start_time',
    'end_time',
    'task_ids',
    'medication_ids',
    'flexibility',
    'user_id',
    'client_id',
    'duration',
    'notes',
    'notes_carer',
    'notes_admin',
    'shift_code',
    'ends_next_day',
    'package_override_id',
    'transport_pickup_id',
    'transport_dropoff_id',
    'location_override_id',
    'package_fixed_id',
    'leave_entry_id',
    'group_session_id',
    'parent_repeat_id',
  ];
  Object.keys(childRow).forEach((key) => {
    if (!copyKeys.includes(key)) delete childRow[key];
  });

  const ignoreKeys = ['id', 'reference_number', 'status_id', 'status'];
  ignoreKeys.forEach((key) => delete childRow[key]);

  state.childRow = childRow;
};

export const appointmentSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    copyAppointment(state, action) {
      copyRow(state, action);
    },
    createChildAppointment(state, action) {
      createChild(state, action);
    },
    clearCopyAppointment(state) {
      delete state.copyRow;
    },
    clearChildAppointment(state) {
      delete state.childRow;
    },
    clearAppointments(state) {
      state.rows = initialState.rows;
    },
    clearAppointment(state) {
      state.row = initialState.row;
    },
    clearMessage(state) {
      state.message = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearNotifications(state) {
      state.notification = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAll.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.rowLoading = 'fulfilled';
      const { rows, total } = action.payload.data;
      state.rows = rows;
      state.total = total;
    });
    builder.addCase(getOne.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.rowLoading = 'fulfilled';

      const { isTemplate, isParent, isChild, data } = action.payload;

      if (isTemplate === true) copyRow(state, { payload: data });
      else if (isParent === true) createChild(state, { payload: data });
      else if (isChild === true) copyRow(state, { payload: data }, false);
      else state.row = action.payload.data;
    });
    builder.addCase(getSignature.pending, (state) => {
      state.loading = 'fulfilled';
      state.rowSignature = null;
    });
    builder.addCase(getSignature.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.rowSignature = action.payload.data;
    });
    builder.addCase(create.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.message = action.payload.message;
    });
    builder.addCase(update.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.message = action.payload.message;
      state.notification = action.payload.notification;
    });
    builder.addCase(acknowledge.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.message = action.payload.message;
    });
    builder.addCase(cancel.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.message = action.payload.message;
    });
    builder.addCase(getEstimate.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.estimate = action.payload.data;
    });
    builder.addCase(startBidding.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.message = action.payload.message;
    });
    builder.addCase(endBidding.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.message = action.payload.message;
    });
    builder.addCase(getStatistics.fulfilled, (state, action) => {
      state.loading = 'fulfilled';
      state.statisticsLoading = 'fulfilled';
      state.statistics = action.payload.data;
    });
    builder.addCase(getStatistics.pending, (state) => {
      state.statisticsLoading = 'pending';
    });
    builder.addCase(notifyUser.fulfilled, (state) => {
      state.loading = 'fulfilled';
    });
    // Default matching for loading cases, pending when action is being called
    builder.addMatcher(isFulfilled, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.error = null;
    });
    builder.addMatcher(isPending, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.loading = 'pending';
    });
    builder.addMatcher(isRejected, (state, action) => {
      if (action.type.startsWith(`${typePrefix}/`)) {
        state.loading = 'declined';
        state.error = action.error.message;
      }
    });
  },
});

export const {
  copyAppointment,
  createChildAppointment,
  clearCopyAppointment,
  clearChildAppointment,
  clearAppointments,
  clearAppointment,
  clearMessage,
  clearError,
  clearNotifications,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
